import {
    LucideDownload, LucidePackagePlus,
    LucideGem, LucideTrash2, LucideImage, LucideImages
} from "lucide-react";
import {Button} from "../ui/Button";
import {ModalCloseButton} from "../modals/ModalBase";
import {Typography} from "../ui/Typography";
import {RoundBadge} from "./RoundBadge";
import {useCart} from "../../context/CartContext";
import React, {useState} from "react";
import {useCreditModal} from "../../context/CreditModalContext";


export const CartSideBar = () => {
    const {isCartSideBarOpen: isOpen, setIsCartSideBarOpen} = useCart();
    const onClose = () => setIsCartSideBarOpen(false);
    const credits = 0;

    return (
        <aside
            id="collection-sidebar"
            className={`z-30 flex-shrink-0 transition-all duration-300 ease-in-out overflow-hidden
            fixed right-0 h-[85vh] min-h-[480px] lg:h-[calc(0.8 * (100vh - 4rem))] lg:self-start top-[72px]
            ${isOpen ? 'w-full opacity-100 pointer-events-auto lg:w-[320px]' : 'w-0 opacity-0 pointer-events-none'}`}
        >
            <MobileOverlay isOpen={isOpen} onClose={onClose}/>

            <div
                id="sidebar-content"
                className="absolute top-[-4px] right-0 w-full lg:w-[320px] flex flex-col h-full 
                bg-surface-800 border-l border-surface-700 lg:rounded-b-lg">

                <SidebarHeader onClose={onClose}/>

                <div className="flex-grow overflow-y-auto">
                    <EmptyCollectionList/>
                    <CollectionList/>
                </div>

                <NoCreditsWarning/>

                <div className="p-4 border-t border-surface-700 flex-shrink-0">
                    <DownloadButton hasCredits={credits > 0} itemsInCart={0} />
                </div>

                </div>
        </aside>
    );
};


const SidebarHeader = ({onClose}: { onClose: () => void }) => {
    return (
        <div className="flex items-center justify-between p-4 border-b border-surface-700 flex-shrink-0">
            <div className="flex items-center gap-3">

                <Typography variant="h3" as="h3">My Cart</Typography>
                
                <input type="text" 
                       id="collection-name-input"
                       className="bg-surface-700 rounded-md px-2 py-1 text-lg font-bold hidden" 
                       value="My First Collection"/>

                {/*if a pack has 50, display amount as 50*/}
                <RoundBadge amount={23} className="flex items-center justify-center text-xm p-1 px-4"/>
                
            </div>
            
            <div className="flex items-center gap-1">
                <ModalCloseButton onClose={onClose} />
            </div>
            
        </div>
    );
};


const MobileOverlay = ({isOpen, onClose}: { isOpen: boolean; onClose: () => void }) => {
    return (
        <div
            id="sidebar-overlay"
            onClick={onClose}
            className={`absolute inset-0 bg-surface-950/60 backdrop-blur-sm transition-opacity duration-300 lg:hidden
                     ${isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}  
        />
    );
};


const EmptyCollectionList = () => {
    return (
        <div id="empty-state" className="flex flex-col items-center justify-center h-full p-8 text-center hidden">
            
            <div className="p-6 rounded-full bg-surface-700 mb-4">
                <LucidePackagePlus className="w-12 h-12" />
            </div>
            
            <Typography variant="h3" as="h3">Your collection is empty</Typography>
            
            <p className="text-sm text-surface-300">
                Find icons you love and click the '+' to add them here.
            </p>
            
        </div>
    );
};


const NoCreditsWarning = () => {
    const {openModal} = useCreditModal();
    const creditsRequiredText = "Not enough credits";
    const noCreditsText = "You need at least 23 credits in order to complete your purchase.";
    
    return (
        <div className="p-4 border-t border-surface-700 flex-shrink-0">
            <div className="p-4 rounded-lg border-2 bg-[var(--red-dark)] border-[var(--red)]">
                <div className="flex items-center gap-3 mb-2">
                    <LucideGem className="h-5 w-5 text-surface-200" />
                    <h4 className="font-bold text-surface-200">{creditsRequiredText}</h4>
                </div>
                <p className="text-sm text-surface-400 mb-4">
                    {noCreditsText}
                </p>

                <Button
                    intent="secondary"
                    size="large"
                    onClick={() => openModal()}
                >
                    Get Credits
                </Button>
                
            </div>
        </div>
    );
};

const DownloadButton = ({hasCredits, itemsInCart}: { hasCredits: boolean; itemsInCart: number }) => {
    return (
        <Button 
            intent={(hasCredits && itemsInCart <= 0) ? "inactive" : "primary"}
            size="large"
            className="gap-2"
        >
            <LucideDownload/>Download icons
        </Button>
    );
};

const CollectionList = () => {
    const generateDummyItems = (count: number): Omit<CartItemProps, 'onDelete'>[] => {
        return Array.from({ length: count }, (_, index) => ({
            id: `${index + 1}`,
            name: index % 2 === 0 ? `Icon ${index + 1}` : `Pack ${index + 1}`,
            price: index % 2 === 0 ? 1 : 15,
        }));
    };

    const [items, setItems] = useState<Omit<CartItemProps, 'onDelete'>[]>(generateDummyItems(23));
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 10;

    const handleDelete = (id: string) => {
        const newItems = items.filter((item) => item.id !== id);
        setItems(newItems);

        const newTotalPages = Math.ceil(newItems.length / itemsPerPage);
        if (currentPage > newTotalPages && newTotalPages > 0) {
            setCurrentPage(newTotalPages);
        } else if (newItems.length === 0) {
            setCurrentPage(1);
        }
    };

    const totalPages = Math.ceil(items.length / itemsPerPage);
    const paginatedItems = items.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

    return (
        <div className="flex flex-col h-full">
            <ul id="collection-list" className="p-2 space-y-1 flex-grow overflow-y-auto">
                {paginatedItems.map((item) => (
                    <CartItem
                        key={item.id}
                        id={item.id}
                        name={item.name}
                        price={item.price}
                        onDelete={handleDelete}
                    />
                ))}
            </ul>
            {totalPages > 1 && (
                <div className="flex justify-center items-center p-2 border-t border-surface-700 gap-2 flex-shrink-0">
                    {Array.from({ length: totalPages }, (_, index) => (
                        <button
                            key={index + 1}
                            onClick={() => setCurrentPage(index + 1)}
                            className={`px-3 py-1 rounded-md ${
                                currentPage === index + 1
                                    ? 'bg-brand-500 text-surface-100'
                                    : 'bg-surface-700 text-surface-300 hover:bg-surface-600'
                            }`}
                        >
                            {index + 1}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
};

interface CartItemProps {
    id: string;
    name : string;
    price: number;
    onDelete: (id: string) => void;
}

const CartItem = (props: CartItemProps) => {
    return (
        <li className="flex items-center justify-between p-2 rounded-md m-2 bg-surface-950/50">
            
            <div className="flex items-center gap-4">

                <span className="flex items-center justify-center w-5 h-5 rounded-md bg-surface-900 text-brand-600">
                    {props.price > 1 ? <LucideImages/> : <LucideImage/>} {/* show pack icon if price > 1 */}
                </span>
                
                <Typography
                    as="p"
                    variant="p"
                    className="text-surface-200 font-medium"
                >
                    {props.name}
                </Typography>

            </div>
            
            <Button
                intent="icon"
                size="xSmall"
                className="text-surface-500 hover:text-surface-100"
                onClick={() => props.onDelete(props.id)}
            >
                <LucideTrash2 className="w-4 h-4" />
            </Button>

        </li>
    );
};


